import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { scoreCartesianGraph } from "@examspark/score";
import { StemCartesianGraph } from "@examspark/types";

import Mark from "../../common/Mark";
import QuestionContext from "../../QuestionContext";
import CartesianGraphComponent from "../common/graph/cartesian/CartesianGraph";
import Toolbar from "../common/graph/toolbar/Toolbar";
import Prompt from "../shared/Prompt";

const Wrapper = styled.div`
  max-width: 55rem;
`;

interface MarkProps {
  mark?: "correct" | "incorrect";
}

const Styled = styled.div<MarkProps>`
  /* display: flex; */
  position: relative;
  padding: 1rem 1rem;
  border-top: solid 3px transparent;
  background-color: #f7f7f7;

  ${(props) => {
    if (props.mark === "correct") {
      return `
      background-color: #e4f3e4;
      border-top: solid 3px #9cb39c;
      `;
    }
    if (props.mark === "incorrect") {
      return `
      background-color: #f3e2e2;
      border-top: solid 3px #b32e2b;
      `;
    }
  }}

  .markup {
    position: absolute;
    top: -11px;
    right: -8px;
  }
`;

export interface ItemCartesianGraphProps {
  data: StemCartesianGraph;
}

const CartesianGraph: React.FC<
  React.PropsWithChildren<ItemCartesianGraphProps>
> = (props) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);
  const [mode, setMode] = useState<string>();

  // Reveal Key
  useEffect(() => {
    if (!player.response) return;
    if (player.revealKey) {
      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: {
            shapes: props.data.scoring?.shapes,
          },
        },
      });
    }
  }, [player.revealKey]);

  let mark: "incorrect" | "correct" | undefined;
  if (player.scoreResponses) {
    mark = "incorrect";

    const score = scoreCartesianGraph(
      props.data,
      player.response?.[props.data.id],
    );
    // console.log("score cartesian", score);
    if (score.max === score.actual) {
      mark = "correct";
    }
  }

  return (
    <>
      <Prompt data={props.data.content.prompt} />
      <Wrapper>
        <Toolbar>
          <Toolbar.Button
            tooltip="Move"
            data-cy="move"
            isPressed={mode === undefined}
            onClick={() => setMode(undefined)}
          >
            <svg viewBox="0 0 16 16" width="16">
              <g>
                <rect x="2.08" y="6.93" width="12" height="2" />
                <path
                  d="M15.74,7.78l-3.28-2.73C12.28,4.9,12,5.03,12,5.27v5.47c0,0.24,0.28,0.37,0.46,0.22l3.28-2.73
		C15.88,8.1,15.88,7.9,15.74,7.78z"
                />
                <path
                  d="M0.26,8.15l3.28,2.73C3.72,11.03,4,10.9,4,10.66V5.2c0-0.24-0.28-0.37-0.46-0.22L0.26,7.71C0.12,7.83,0.12,8.03,0.26,8.15z
		"
                />
              </g>
              <g>
                <rect
                  x="2.12"
                  y="7.01"
                  transform="matrix(-1.836970e-16 1 -1 -1.836970e-16 16.1263 -0.1042)"
                  width="12"
                  height="2"
                />
                <path
                  d="M8.26,15.68l2.73-3.21c0.16-0.18,0.03-0.46-0.21-0.46H5.32c-0.24,0-0.37,0.28-0.21,0.46l2.73,3.21
		C7.94,15.81,8.15,15.81,8.26,15.68z"
                />
                <path d="M7.89,0.14L5.34,3.55C5.2,3.74,5.33,4,5.56,4h5.11c0.23,0,0.36-0.26,0.23-0.45L8.34,0.14C8.23-0.01,8-0.01,7.89,0.14z" />
              </g>
            </svg>
          </Toolbar.Button>

          <Toolbar.Button
            tooltip="Point"
            data-cy="point"
            isPressed={mode === "point"}
            onClick={() => {
              if (mode === "point") {
                setMode(undefined);
              } else {
                setMode("point");
              }
            }}
          >
            <div
              style={{
                width: "16px",
                height: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg viewBox="0 0 16 16" width="14">
                <circle cx="8" cy="8" r="5" />
              </svg>
            </div>
          </Toolbar.Button>
          <Toolbar.Button
            tooltip="Line"
            data-cy="line"
            isPressed={mode === "line"}
            onClick={() => {
              if (mode === "line") {
                setMode(undefined);
              } else {
                setMode("line");
              }
            }}
          >
            <svg viewBox="0 0 16 16" width="16">
              <rect x="2.08" y="6.93" width="12" height="2" />
              <path
                d="M15.65,7.77l-4.21-2.81C11.25,4.83,11,4.97,11,5.19v5.62c0,0.22,0.25,0.36,0.44,0.23l4.21-2.81
	C15.82,8.12,15.82,7.88,15.65,7.77z"
              />
              <path d="M0.35,8.16l4.21,2.81C4.75,11.1,5,10.96,5,10.74V5.12C5,4.9,4.75,4.76,4.56,4.89L0.35,7.7C0.18,7.81,0.18,8.05,0.35,8.16z" />
            </svg>
          </Toolbar.Button>
          <Toolbar.Button
            tooltip="Ray"
            data-cy="ray"
            isPressed={mode === "ray"}
            onClick={() => {
              if (mode === "ray") {
                setMode(undefined);
              } else {
                setMode("ray");
              }
            }}
          >
            <svg viewBox="0 0 16 16" width="16">
              <rect x="2.08" y="6.93" width="12" height="2" />
              <path
                d="M15.65,7.77l-4.21-2.81C11.25,4.83,11,4.97,11,5.19v5.62c0,0.22,0.25,0.36,0.44,0.23l4.21-2.81
	C15.82,8.12,15.82,7.88,15.65,7.77z"
              />
            </svg>
          </Toolbar.Button>
          <Toolbar.Button
            tooltip="Line Segment"
            data-cy="line-segment"
            isPressed={mode === "line-segment"}
            onClick={() => {
              if (mode === "line-segment") {
                setMode(undefined);
              } else {
                setMode("line-segment");
              }
            }}
          >
            <svg viewBox="0 0 16 16" width="16">
              <rect x="2" y="7" width="12" height="2" />
              <circle cx="3" cy="8" r="3" />
              <circle cx="13" cy="8" r="3" />
            </svg>
          </Toolbar.Button>
          <Toolbar.Button
            tooltip="Circle"
            data-cy="circle"
            isPressed={mode === "circle"}
            onClick={() => {
              if (mode === "circle") {
                setMode(undefined);
              } else {
                setMode("circle");
              }
            }}
          >
            <svg viewBox="0 0 16 16" width="16">
              <path
                d="M8,15c-3.86,0-7-3.14-7-7c0-3.86,3.14-7,7-7c3.86,0,7,3.14,7,7C15,11.86,11.86,15,8,15z M8,3C5.24,3,3,5.24,3,8s2.24,5,5,5
		s5-2.24,5-5S10.76,3,8,3z"
              />
            </svg>
          </Toolbar.Button>
          <Toolbar.Button
            tooltip="Polygon"
            data-cy="polygon"
            isPressed={mode === "polygon"}
            onClick={() => {
              if (mode === "polygon") {
                setMode(undefined);
              } else {
                setMode("polygon");
              }
            }}
          >
            <svg viewBox="0 0 16 16" width="16">
              <path d="M12.59,14.94H3.41L0.58,6.22L8,0.82l7.42,5.39L12.59,14.94z M4.87,12.94h6.27l1.94-5.96L8,3.29L2.93,6.98L4.87,12.94z" />
            </svg>
          </Toolbar.Button>
          <Toolbar.Button
            tooltip="Erase"
            data-cy="erase"
            isPressed={mode === "erase"}
            onClick={() => {
              if (mode === "erase") {
                setMode(undefined);
              } else {
                setMode("erase");
              }
            }}
          >
            <svg viewBox="0 0 421.542 421.542" width="16">
              <path
                id="path1155"
                d="m106.584 61.396c-24.196 0-46.346 13.775-57.167 35.417l-42.667 85.375c-9 18.001-9 39.166 0 57.167l42.667 85.333c10.821 21.642 32.97 35.458 57.167 35.458h251.042c35.094 0 63.917-28.99 63.917-64.083v-170.709c0-35.094-28.823-63.958-63.917-63.958zm122.833 85.042c5.785.044 11.303 2.435 15.292 6.625l27.542 27.5 27.542-27.5c3.864-3.977 9.124-6.293 14.667-6.458 11.778-.346 21.607 8.921 21.953 20.699.175 5.937-2.134 11.678-6.37 15.842l-27.583 27.583 27.583 27.5c8.353 8.353 8.353 21.897 0 30.25s-21.897 8.353-30.25 0l-27.542-27.542-27.542 27.542c-8.353 8.353-21.897 8.353-30.25 0s-8.353-21.897 0-30.25l27.583-27.5-27.583-27.583c-8.491-8.17-8.75-21.677-.58-30.167 4.062-4.221 9.68-6.586 15.538-6.541z"
              />
            </svg>
          </Toolbar.Button>
        </Toolbar>
        <Styled mark={mark}>
          {mark && <Mark type={mark} />}
          <CartesianGraphComponent
            {...props.data.config.graph}
            shapes={player.response?.[props.data.id]?.shapes || []}
            mode={mode}
            onChange={(shapes) => {
              // some shape tools stay selected
              if (!["point", "move", "erase"].includes(mode ?? "")) {
                setMode(undefined);
              }
              dispatchPlayer({
                type: "RESPONSE",
                data: {
                  id: props.data.id,
                  response: {
                    shapes: shapes,
                  },
                },
              });
            }}
          />
        </Styled>
      </Wrapper>
    </>
  );
};

export default CartesianGraph;
